import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { Siren } from 'lucide-react';

// FE-17 — el gesto. "Confirmación sí, fricción no": un botón de pánico que se
// dispara solo en el bolsillo destruye su propia credibilidad, pero quien lo
// pulsa puede estar accidentado y no tiene puntería ni paciencia para un
// formulario.
//
// Decisión: MANTENER PULSADO 3 s con anillo de progreso. Es el único gesto que
// se alcanza con un pulgar sin mirar y que a la vez es físicamente incompatible
// con el bolsillo — exige contacto sostenido y quieto en un punto. Se
// descartaron el doble toque (se dispara solo) y el deslizamiento (exige un
// arrastre preciso).

const HOLD_MS = 3000;
// Si el dedo se va de paseo, no era una emergencia: era el bolsillo.
const CANCEL_PX = 24;

interface PanicButtonProps {
  onConfirm: () => void;
  disabled?: boolean;
  busy?: boolean;
}

export function PanicButton({ onConfirm, disabled = false, busy = false }: PanicButtonProps) {
  const ref = useRef<HTMLButtonElement>(null);
  // QUIÉN decide el disparo y QUIÉN lo pinta son dos relojes distintos, a
  // propósito. El navegador congela `requestAnimationFrame` cuando la ventana
  // pasa a segundo plano —comprobado: el anillo se quedaba en 0 y el gesto no
  // completaba nunca—, así que el temporizador manda y el rAF solo dibuja. Si
  // el pintado se congela, el conductor que sostuvo el dedo tres segundos
  // igual declara su emergencia.
  const disparo = useRef<number | null>(null);
  const raf = useRef<number | null>(null);
  const inicio = useRef(0);
  const origen = useRef<{ x: number; y: number } | null>(null);
  const [holding, setHolding] = useState(false);

  const parar = useCallback(() => {
    if (disparo.current !== null) clearTimeout(disparo.current);
    if (raf.current !== null) cancelAnimationFrame(raf.current);
    disparo.current = null;
    raf.current = null;
    origen.current = null;
    ref.current?.style.setProperty('--panic-progress', '0');
    setHolding(false);
  }, []);

  const pintar = useCallback(() => {
    const avance = Math.min((performance.now() - inicio.current) / HOLD_MS, 1);
    // El progreso se escribe DIRECTAMENTE en el DOM, no con useState: 60
    // re-renders por segundo en la pantalla del conductor no tienen ninguna
    // justificación, y el CSS ya sabe pintar la variable.
    ref.current?.style.setProperty('--panic-progress', String(avance));
    if (avance < 1) raf.current = requestAnimationFrame(pintar);
  }, []);

  const arrancar = useCallback(() => {
    if (disabled || busy) return;
    // Si quedó un pulso anterior a medias, se descarta y este empieza limpio en
    // vez de salir por la puerta de atrás: un botón de pánico que se queda
    // mudo porque un temporizador se colgó es peor que uno que se reinicia.
    if (disparo.current !== null) parar();
    setHolding(true);
    navigator.vibrate?.(25); // iOS no lo soporta: es realimentación opcional, no parte del flujo
    inicio.current = performance.now();
    raf.current = requestAnimationFrame(pintar);
    disparo.current = window.setTimeout(() => {
      parar();
      navigator.vibrate?.([120, 60, 160]);
      onConfirm();
    }, HOLD_MS);
  }, [disabled, busy, pintar, parar, onConfirm]);

  // Si la pantalla se bloquea o la pestaña pasa al fondo, el rAF se congela y
  // el pulso quedaría a medias esperando volver. Se cancela explícitamente.
  useEffect(() => {
    const alOcultar = () => { if (document.hidden) parar(); };
    document.addEventListener('visibilitychange', alOcultar);
    return () => {
      document.removeEventListener('visibilitychange', alOcultar);
      parar();
    };
  }, [parar]);

  return (
    <button
      ref={ref}
      type="button"
      className={clsx('panic-fab', holding && 'panic-fab--holding')}
      disabled={disabled || busy}
      aria-label="Declarar emergencia: mantén pulsado tres segundos"
      aria-describedby="panic-fab-hint"
      onPointerDown={e => {
        // Con captura, el pointerup llega aunque el dedo termine fuera del
        // botón; sin ella el gesto se quedaría colgado con el anillo lleno.
        // Va en try/catch porque `setPointerCapture` lanza `NotFoundError` si
        // el pointer ya no está activo, y dejar que una comodidad tumbe el
        // botón de pánico sería absurdo: sin captura funciona igual, solo peor.
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* sin captura */ }
        origen.current = { x: e.clientX, y: e.clientY };
        arrancar();
      }}
      onPointerMove={e => {
        if (!origen.current) return;
        if (Math.hypot(e.clientX - origen.current.x, e.clientY - origen.current.y) > CANCEL_PX) parar();
      }}
      onPointerUp={parar}
      onPointerCancel={parar}
      onLostPointerCapture={parar}
      // En iOS el long-press abre el menú contextual y mata el gesto.
      onContextMenu={e => e.preventDefault()}
      // El preventDefault mata también el `click` sintético de Enter/Espacio:
      // así NO existe ningún camino de una sola pulsación hasta onConfirm.
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); arrancar(); } }}
      onKeyUp={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); parar(); } }}
      onBlur={parar}
    >
      <span className="panic-fab__core">
        <Siren size={26} aria-hidden />
        <span className="panic-fab__label">SOS</span>
      </span>
      <span id="panic-fab-hint" className="sr-only">
        Mantén pulsado tres segundos para declarar una emergencia. Suelta antes para cancelar.
      </span>
    </button>
  );
}
